import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Upload, Store, Beer, Umbrella } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API_BASE, withFileAuth } from '@/utils/api';

export const errText = (e, fallback = 'No se pudo guardar') => {
  const d = e.response?.data?.detail;
  return typeof d === 'string' ? d : Array.isArray(d) ? d.map(x => x.msg).join(' ') : fallback;
};

const TYPES = [['restaurant', 'Restaurante', Store], ['bar', 'Bar', Beer], ['parador', 'Parador de playa', Umbrella]];
const TAX = [['monotributo', 'Monotributo'], ['responsable_inscripto', 'Responsable inscripto']];

const Choice = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2">
    {options.map(([k, label, Icon]) => (
      <button type="button" key={k} onClick={() => onChange(k)} data-testid={`${testid}-${k}`}
        className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl border text-sm font-medium transition-colors ${value === k ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:bg-muted'}`}>
        {Icon && <Icon className="w-4 h-4" />}{label}
      </button>
    ))}
  </div>
);

export const StepBusiness = ({ initial, onDone }) => {
  const [form, setForm] = useState({ name: initial?.name || '', business_type: initial?.business_type === 'beach_club' ? 'parador' : (initial?.business_type || 'restaurant'), tax_condition: initial?.tax_condition || 'monotributo', logo_url: initial?.logo_url || null });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('El logo debe ser una imagen');
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('purpose', 'logo');
    try {
      const { data } = await axios.post(`${API_BASE}/upload/image`, fd);
      setForm(f => ({ ...f, logo_url: data.url }));
    } catch (e) { toast.error(errText(e, 'No se pudo subir el logo')); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API_BASE}/onboarding/business`, form);
      onDone();
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="step-business">
      <div className="space-y-2"><Label>Nombre del negocio</Label>
        <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ej: Parador Sol & Mar" data-testid="business-name-input" /></div>
      <div className="space-y-2"><Label>Rubro</Label><Choice options={TYPES} value={form.business_type} onChange={v => setForm({ ...form, business_type: v })} testid="business-type" /></div>
      <div className="space-y-2"><Label>Condición fiscal</Label><Choice options={TAX} value={form.tax_condition} onChange={v => setForm({ ...form, tax_condition: v })} testid="tax-condition" /></div>
      <div className="space-y-2"><Label>Logo</Label>
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-2xl border border-dashed border-border grid place-items-center overflow-hidden bg-muted" data-testid="logo-preview">
            {form.logo_url ? <img src={withFileAuth(form.logo_url)} alt="Logo" className="w-full h-full object-contain" /> : <Store className="w-7 h-7 text-muted-foreground" />}
          </div>
          <label className="cursor-pointer">
            <input type="file" accept="image/*" className="hidden" onChange={e => upload(e.target.files?.[0])} data-testid="logo-file-input" />
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm hover:bg-muted">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{form.logo_url ? 'Cambiar logo' : 'Subir logo'}
            </span>
          </label>
        </div>
      </div>
      <Button onClick={save} disabled={busy || form.name.trim().length < 2} className="rounded-full" data-testid="business-save-btn">
        {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Guardar y seguir
      </Button>
    </div>
  );
};

export const StepTables = ({ businessType, onDone }) => {
  const parador = businessType === 'parador';
  const [form, setForm] = useState(parador
    ? { space_name: 'Playa', prefix: 'Sombrilla', start: 1, count: 40, capacity: 4, unit_type: 'umbrella' }
    : { space_name: 'Salón', prefix: 'Mesa', start: 1, count: 12, capacity: 4, unit_type: 'table' });
  const [created, setCreated] = useState([]);
  const [busy, setBusy] = useState(false);
  const num = (k) => (e) => setForm({ ...form, [k]: parseInt(e.target.value, 10) || 0 });

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}/onboarding/tables`, form);
      setCreated(c => [...c, `${data.space_name}: ${data.created} creadas${data.skipped_existing ? ` (${data.skipped_existing} ya existían)` : ''}`]);
      toast.success(`${data.created} unidades creadas`);
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="step-tables">
      <p className="text-sm text-muted-foreground">
        {parador ? 'Ej: "Sombrillas del 1 al 40" en el espacio Playa.' : 'Ej: "Salón, 12 mesas, capacidad 4".'} Podés crear varios espacios.
      </p>
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="space-y-2"><Label>Espacio</Label><Input value={form.space_name} onChange={e => setForm({ ...form, space_name: e.target.value })} data-testid="tables-space-input" /></div>
        <div className="space-y-2"><Label>Nombre de cada unidad</Label><Input value={form.prefix} onChange={e => setForm({ ...form, prefix: e.target.value })} data-testid="tables-prefix-input" /></div>
        <div className="space-y-2"><Label>Tipo</Label>
          <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={form.unit_type} onChange={e => setForm({ ...form, unit_type: e.target.value })} data-testid="tables-type-select">
            <option value="table">Mesa</option><option value="umbrella">Sombrilla</option><option value="tent">Carpa</option>
          </select></div>
        <div className="space-y-2"><Label>Desde el número</Label><Input type="number" min={0} value={form.start} onChange={num('start')} data-testid="tables-start-input" /></div>
        <div className="space-y-2"><Label>Cantidad</Label><Input type="number" min={1} max={300} value={form.count} onChange={num('count')} data-testid="tables-count-input" /></div>
        <div className="space-y-2"><Label>Capacidad</Label><Input type="number" min={1} value={form.capacity} onChange={num('capacity')} data-testid="tables-capacity-input" /></div>
      </div>
      <p className="text-sm" data-testid="tables-preview">Se crearán: {form.prefix} {form.start} … {form.prefix} {form.start + Math.max(form.count, 1) - 1}</p>
      <div className="flex flex-wrap gap-2">
        <Button onClick={create} disabled={busy || form.count < 1} variant={created.length ? 'outline' : 'default'} className="rounded-full" data-testid="tables-create-btn">
          {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Crear {form.count} {form.unit_type === 'umbrella' ? 'sombrillas' : form.unit_type === 'tent' ? 'carpas' : 'mesas'}
        </Button>
        {created.length > 0 && <Button onClick={onDone} className="rounded-full" data-testid="tables-continue-btn">Seguir</Button>}
      </div>
      {created.map((c, i) => <p key={i} className="text-sm text-green-700" data-testid={`tables-created-${i}`}>{c}</p>)}
    </div>
  );
};
